// packages/report-components/src/pageClamp.ts
//
// The two things every text slot on a report PAGE needs (plan 2026-09-19
// report page house style): a height that is a whole number of fixed px lines
// whatever the text is, and a way to say that the text did not fit.
//
// Why both. A page cell clips and nothing reflows, so the server budgets a
// block from line counts priced in px (`layout.py`, "Page density"). A slot
// drawn with a unitless line-height and no clamp is as tall as its text, and
// the budget is then a hope. With `pageLines` the slot is `lines x linePx`
// tall by construction. But a clamped slot never overflows its BOX, so the
// editor's overflow measurement (scrollHeight of the block against its cell)
// reads zero for exactly the text that was cut: `useTruncatedFlag` measures
// the slot itself and says so as data (`data-truncated="1"`), which the
// canvas reads to warn the author before a reader sees an ellipsis.

import * as React from "react";

/** Inline style for a slot of exactly `lines` lines of `linePx` each.
 *  `fixed` keeps the height when the text is shorter, so a repeated part (a
 *  list item) is the same height whatever it says; prose that may be shorter
 *  than its budget passes `fixed: false` and only gets the ceiling. */
export function pageLines(
  lines: number,
  linePx: number,
  opts: { fixed?: boolean } = {},
): React.CSSProperties {
  const n = Math.max(1, Math.floor(lines));
  const px = n * linePx;
  return {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: n,
    overflow: "hidden",
    lineHeight: `${linePx}px`,
    ...(opts.fixed === false ? { maxHeight: px } : { height: px }),
    wordBreak: "break-word",
  };
}

/** A ref callback for a clamped slot. After layout it sets
 *  `data-truncated="1"` on the element when its text did not fit, and removes
 *  it when it did. A no-op where nothing is laid out (jsdom: every box is 0). */
export function useTruncatedFlag<T extends HTMLElement>(signature: unknown): (el: T | null) => void {
  const [el, setEl] = React.useState<T | null>(null);
  React.useLayoutEffect(() => {
    if (!el) return;
    const read = () => {
      const cut =
        el.clientHeight > 0 &&
        (el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1);
      if (cut) el.setAttribute("data-truncated", "1");
      else el.removeAttribute("data-truncated");
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [el, signature]);
  return setEl;
}
